<!-- GUESS THE THING — it comes into focus one clue at a time.

     THE UN-BLURRING IS REAL. The glyph is rendered enormous behind a CSS blur
     that steps down with every clue revealed, so the picture genuinely sharpens
     the way the brief describes. No image files, nothing fetched, works with no
     signal — which was the deciding constraint, since this app has to run on a
     plane.

     Clues arrive on their own every four seconds so there is no waiting it out,
     and every clue that lands costs him points. Guessing early is the whole
     game. -->
<template>
  <IonPage>
    <AppHeader title="Guess the Thing" back="/tabs/play" />

    <IonContent :fullscreen="true">
      <template v-if="!started">
        <div class="pad" style="padding-top: var(--sp-6)">
          <p class="ex-display lg">Guess the Thing</p>
          <p class="ex-sub">
            It starts as a blur. Every four seconds another clue lands and it
            gets sharper — and worth less. Guess on the first clue for triple
            points. Three lives.
          </p>
        </div>
        <ModeRecord mode="guess-the-thing" />

        <div class="pad" style="margin-top: var(--sp-5)">
          <IonButton expand="block" size="large" @click="begin">Start guessing</IonButton>
        </div>
      </template>

      <template v-else-if="current">
        <GameHud :score="score" :streak="streak" :lives="lives" />

        <div class="q-stage" ref="stageRef" tabindex="-1">
          <div class="guess-glyph" :style="{ '--blur': blurPx }" aria-hidden="true">
            {{ current.glyph }}
          </div>

          <p class="center ex-eyebrow" style="margin: 0">
            {{ revealed ? 'The answer' : `Worth ${Math.round(multiplier * 100)}%` }}
          </p>

          <div class="clue-list">
            <div v-for="n in cluesShown" :key="n" class="clue">
              <span class="n" aria-hidden="true">{{ n }}</span>
              <span>{{ current.clues[n - 1] }}</span>
            </div>
          </div>

          <!-- Untimed only. With the clock off nothing arrives on its own, so
               taking a clue has to be something he chooses to do — and the
               label says what it costs before he taps it. -->
          <div v-if="!timed && canTakeClue" class="pad" style="margin-top: var(--sp-3)">
            <IonButton expand="block" fill="outline" @click="revealNextClue()">
              Give me another clue (drops to {{ nextClueWorth }}%)
            </IonButton>
          </div>

          <div class="answers two-up">
            <button
              v-for="opt in shuffled"
              :key="opt"
              type="button"
              class="answer"
              :class="optClass(opt)"
              :disabled="revealed"
              @click="guess(opt)"
            >
              {{ opt }}
              <span v-if="revealed && optClass(opt) === 'right'" class="sr-only"> — correct answer</span>
              <span v-else-if="revealed && optClass(opt) === 'wrong'" class="sr-only"> — your answer, incorrect</span>
            </button>
          </div>
        </div>
      </template>

      <div class="tail" />
    </IonContent>

    <VerdictFlash :text="verdict.text" :why="verdict.why" :good="verdict.good" />
    <AusBonus :when="ausNow" :flag="ausFlag" @done="ausNow = false" />
  </IonPage>
</template>

<script setup>
import { ref, computed, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { IonPage, IonContent, IonButton } from '@ionic/vue';
import { AppHeader, announce } from '../core/ui';
import GameHud from '../components/GameHud.vue';
import ModeRecord from '../components/ModeRecord.vue';
import VerdictFlash from '../components/VerdictFlash.vue';
import AusBonus from '../components/AusBonus.vue';
import { drawQuestions, pointsFor, isAusAnswer, shuffle } from '../game/engine.js';
import { drawOptions } from '../game/profile.js';
import { finishRun } from '../game/run.js';
import { useStopOnLeave, useResetOnEnter } from '../game/guard.js';
import { pun } from '../game/puns.js';
import { haptic } from '../core/util.js';
import { paced, timed } from '../game/pace.js';
import { regainLife } from '../game/lives.js';

const router = useRouter();
const CLUE_EVERY_MS = 4000;
const MAX_CLUES = 5;

const started = ref(false);
const queue = ref([]);
const idx = ref(0);
const cluesShown = ref(1);
const revealed = ref(false);
const chosen = ref('');
const shuffled = ref([]);
const ausNow = ref(false);
const ausFlag = ref('🇦🇺');
const stageRef = ref(null);

const score = ref(0);
const streak = ref(0);
const bestChain = ref(0);
const correct = ref(0);
const wrong = ref(0);
const ausCount = ref(0);
const lives = ref(3);
const verdict = ref({ text: '', why: '', good: true });

const current = computed(() => queue.value[idx.value] ?? null);
const answerText = computed(() => current.value?.options[current.value.answer] ?? '');

// Fully sharp once he has answered, so the reveal is part of the payoff.
const blurPx = computed(() => {
  if (revealed.value) return '0px';
  return `${Math.max(0, 20 - (cluesShown.value - 1) * 4.5)}px`;
});

// Guess on clue 1 and it is worth triple; ride it to clue 5 and it is worth
// face value. That gradient is the entire tension of the mode.
const multiplier = computed(() => [3, 2.5, 2, 1.5, 1][cluesShown.value - 1] ?? 1);

let clueTimer = null;
let advanceTimer = null;
let ended = false;

/** Reveal the next clue. Costs points — see `multiplier`. */
function revealNextClue() {
  if (revealed.value || ended || cluesShown.value >= MAX_CLUES) return;
  cluesShown.value += 1;
  // Every clue that lands is worth points on its own, and the blur stepping
  // down is a purely visual tell — without this a screen-reader user never
  // finds out a new clue arrived at all.
  announce(`Clue ${cluesShown.value}: ${current.value?.clues[cluesShown.value - 1] ?? ''}`);
}

// Timed: clues arrive on their own and the pressure is to guess before the
// next one costs you. Untimed: nothing arrives unless he asks for it, which
// turns the same scoring into a deliberate trade rather than a countdown —
// the mode keeps its point, and loses only the hurry.
const canTakeClue = computed(() => !revealed.value && !ended && cluesShown.value < MAX_CLUES);
const nextClueWorth = computed(
  () => Math.round(([3, 2.5, 2, 1.5, 1][cluesShown.value] ?? 1) * 100),
);

function scheduleClue() {
  clearTimeout(clueTimer);
  if (!timed.value) return;
  if (cluesShown.value >= MAX_CLUES || revealed.value || ended) return;
  clueTimer = setTimeout(() => {
    if (revealed.value || ended) return;
    revealNextClue();
    scheduleClue();
  }, paced(CLUE_EVERY_MS));
}

function deal() {
  cluesShown.value = 1;
  revealed.value = false;
  chosen.value = '';
  shuffled.value = shuffle(current.value?.options ?? []);
  announce(`New thing. Clue 1: ${current.value?.clues[0] ?? ''}`);
  scheduleClue();
}

function begin() {
  queue.value = drawQuestions('guess-the-thing', { ...drawOptions.value, count: 24 });
  started.value = true;
  deal();
  // The Start button is gone the instant this runs, so without this a
  // keyboard or screen-reader user is dropped back to <body>.
  nextTick(() => stageRef.value?.focus());
}

function optClass(opt) {
  if (!revealed.value) return '';
  if (opt === answerText.value) return 'right';
  if (opt === chosen.value) return 'wrong';
  return 'muted';
}

function guess(opt) {
  if (revealed.value || ended || !current.value) return;
  clearTimeout(clueTimer);
  const q = current.value;
  revealed.value = true;
  chosen.value = opt;

  if (opt === answerText.value) {
    const aus = isAusAnswer(q, opt);
    if (aus) {
      ausCount.value += 1;
      ausFlag.value = /austria/i.test(opt) ? '🇦🇹' : '🇦🇺';
      ausNow.value = true;
    }
    score.value += pointsFor({
      difficulty: q.difficulty,
      streak: streak.value,
      aus,
      multiplier: multiplier.value,
    });
    streak.value += 1;
    regainLife(lives);
    bestChain.value = Math.max(bestChain.value, streak.value);
    correct.value += 1;
    haptic('success');
    verdict.value = {
      text: cluesShown.value === 1 ? 'ON THE FIRST CLUE. Austin-ishing.' : pun('correct'),
      why: q.reason,
      good: true,
    };
  } else {
    streak.value = 0;
    wrong.value += 1;
    lives.value -= 1;
    haptic('error');
    verdict.value = { text: pun('wrong'), why: `${answerText.value}. ${q.reason}`, good: false };
  }

  clearTimeout(advanceTimer);
  advanceTimer = setTimeout(() => (lives.value <= 0 ? end() : next()), 2300);
}

function next() {
  if (ended) return;
  verdict.value = { text: '', why: '', good: true };
  idx.value += 1;
  if (idx.value >= queue.value.length) {
    queue.value = [...queue.value, ...drawQuestions('guess-the-thing', { ...drawOptions.value, count: 20 })];
  }
  deal();
}

async function end() {
  if (ended) return;
  ended = true;
  clearTimeout(clueTimer);
  clearTimeout(advanceTimer);
  await finishRun({
    mode: 'guess-the-thing',
    score: score.value,
    lastedMs: 0,
    correct: correct.value,
    wrong: wrong.value,
    bestChain: bestChain.value,
    ausBonuses: ausCount.value,
  });
  router.replace('/tabs/play/results');
}

// Everything this screen has running, stopped. Idempotent: it is called on
// every leave, on abandon, and again before every re-entry.
function teardown() {
  ended = true;
  clearTimeout(advanceTimer);
  clearTimeout(clueTimer);
}

// Back to the pre-game screen. Without this, "Go again" returned to this same
// cached instance still holding the finished run.
function resetRun() {
  teardown();
  ended = false;
  // Every ref back to the literal it was declared with — the state a
  // fresh mount would have. Resetting only the run flags left the LAST
  // question's verdict and its revealed answer painted over the new
  // board, so a new game opened showing why the previous one ended.
  started.value = false;
  queue.value = [];
  idx.value = 0;
  cluesShown.value = 1;
  revealed.value = false;
  chosen.value = '';
  shuffled.value = [];
  ausNow.value = false;
  ausFlag.value = '🇦🇺';
  stageRef.value = null;
  score.value = 0;
  streak.value = 0;
  bestChain.value = 0;
  correct.value = 0;
  wrong.value = 0;
  ausCount.value = 0;
  lives.value = 3;
  verdict.value = { text: '', why: '', good: true };
}

useStopOnLeave(teardown);
useResetOnEnter(resetRun);

onUnmounted(() => {
  ended = true;
  clearTimeout(clueTimer);
  clearTimeout(advanceTimer);
});
</script>
